/**
 * zilius-gsap-animations.js  —  v2.0
 * Animazioni GSAP premium per Zilius.
 * Nuove features: word reveal, magnetic buttons, 3D card tilt,
 * custom cursor, parallax multi-layer hero, counter animato,
 * navbar scroll-hide, section color transitions.
 */
(function () {
  'use strict';

  /* ─────────────────────────────────────────
   *  COSTANTI DI CONFIGURAZIONE
   * ───────────────────────────────────────── */
  const READY_CLASS     = 'zilius-motion-ready';
  const REDUCED_CLASS   = 'zilius-motion-reduced';
  const INITIALIZED_FLAG = '__ziliusGsapMotionLoaded';

  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointerQuery   = window.matchMedia('(any-pointer: fine)');

  const MOTION_EXCLUDE_SELECTOR =
    '#header-group, footer, dialog-component, .quick-add-modal, dialog, .skip-to-content-link, [data-zilius-scroll-home]';

  const PAGE_MOTION_SELECTORS = [
    '.section-content-wrapper > :is(header, .text-block, .group-block, .image-block, .button, .button-secondary, rte-formatter, .blog-post-comments-container)',
    ':is(.product-grid__item, .resource-list__item, .blog-post-item)',
    ':is(.cart-page__title, .cart-page__items, .cart-page__summary, .cart-page__more-blocks)',
    '.product-information__media',
    '.product-details > .group-block',
    ':is(.facets-block-wrapper, .main-collection-grid__title, .main-collection-grid__empty, .pagination)',
    ':is(.search-page__header, .search-page-input__parent)',
  ];

  const MAIN_REVEAL_SELECTOR       = PAGE_MOTION_SELECTORS.map((s) => `#MainContent ${s}`).join(', ');
  const FIRST_SECTION_INTRO_SELECTOR = PAGE_MOTION_SELECTORS.join(', ');
  const IMAGE_LOAD_SELECTOR        = '#MainContent img';

  /* ─────────────────────────────────────────
   *  BOOTSTRAP
   * ───────────────────────────────────────── */
  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
      return;
    }
    callback();
  }

  function waitForGsap(attempt = 0) {
    if (window.gsap) { init(window.gsap); return; }
    if (attempt < 60) {
      window.setTimeout(() => waitForGsap(attempt + 1), 50);
      return;
    }

    const root = document.documentElement;
    root.classList.add('zilius-motion-timeout');
    root.classList.remove('zilius-motion-primed');
  }

  function shouldReduceMotion() {
    return (
      reducedMotionQuery.matches ||
      Number(navigator.hardwareConcurrency) <= 2 ||
      Number(navigator.deviceMemory) <= 2
    );
  }


  /* ─────────────────────────────────────────
   *  INIT PRINCIPALE
   * ───────────────────────────────────────── */
  function init(gsap) {
    if (window[INITIALIZED_FLAG]) return;
    window[INITIALIZED_FLAG] = true;

    if (isShopifyDesignMode()) {
      document.documentElement.classList.add(READY_CLASS);
      document.documentElement.classList.remove('zilius-motion-primed');
      return;
    }

    if (shouldReduceMotion()) {
      document.documentElement.classList.add(REDUCED_CLASS, READY_CLASS);
      document.documentElement.classList.remove('zilius-motion-primed');
      return;
    }

    const ScrollTrigger = window.ScrollTrigger;

    if (ScrollTrigger) {
      gsap.registerPlugin(ScrollTrigger);
      ScrollTrigger.config({ ignoreMobileResize: true });
    }

    gsap.config({ nullTargetWarn: false });
    gsap.defaults({ ease: 'power3.out' });

    const root = document.documentElement;
    const primingTimedOut = root.classList.contains('zilius-motion-timeout');
    const isInternalNavigation = isSameOriginNavigation();
    root.classList.add(READY_CLASS);
    root.classList.remove('zilius-motion-primed');

    enhanceMediaFrames();

    if (!primingTimedOut) {
      if (isInternalNavigation) {
        const visibleItems = getMotionCandidates(document, MAIN_REVEAL_SELECTOR)
          .filter((element) => element.getBoundingClientRect().top < window.innerHeight * 1.05);
        markMotionHandled(visibleItems);
      } else {
        runPageIntro(gsap);
      }

      setupScrollReveals(gsap, ScrollTrigger, document);
      setupMediaCardGalleryReveals(gsap, ScrollTrigger, document);
    }

    // setupInteractiveMotion(gsap); // Interactive motion disabled
    // setupMagneticButtons(gsap); // Magnetic buttons disabled
    // setupCardTilt3D(gsap); // 3D tilt effect disabled
    setupCounters(gsap, ScrollTrigger);
    // setupNavbarScrollBehavior(gsap, ScrollTrigger); // Navbar scroll behavior disabled
    setupHeroParallaxText(gsap, ScrollTrigger);
    setupShopifyEditorRefresh(gsap, ScrollTrigger);

    scheduleNonCriticalMotion(() => {
      setupImageDepth(gsap, ScrollTrigger, document);
    });

    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      refreshScrollTriggers(ScrollTrigger);
    });
  }


  /* ─────────────────────────────────────────
   *  PAGE INTRO (invariato + migliorato)
   * ───────────────────────────────────────── */
  function runPageIntro(gsap, options = {}) {
    const firstSection  = document.querySelector('#MainContent > .shopify-section:first-child');
    const hero          = firstSection?.querySelector('.hero');
    const durationScale = options.quick ? 0.72 : 1;
    const tl = gsap.timeline({ defaults: { overwrite: 'auto' }, delay: options.quick ? 0 : 0.02 });

    if (hero) {
      const media          = hero.querySelector('.hero__media-wrapper');
      const navigationItems = getHeroNavigationItems(hero);
      const contentItems   = getUniqueElements(
        hero.querySelectorAll(
          '.hero__content-wrapper > :is(.text-block, .group-block, .button, .button-secondary, image-block, jumbo-text, logo-block)'
        )
      ).filter((el) => !navigationItems.includes(el));

      markMotionHandled([...navigationItems, ...contentItems]);

      if (media) {
        tl.fromTo(
          media,
          { scale: 1.024 },
          { scale: 1,
            duration: 0.76 * durationScale, ease: 'power2.out',
            clearProps: 'transform' },
          0
        );
      }

      if (contentItems.length) {
        animateContainedMedia(gsap, contentItems, { duration: 0.64, stagger: 0.035 });
        tl.fromTo(
          contentItems,
          { autoAlpha: 0, y: 24 },
          { autoAlpha: 1, y: 0,
            duration: 0.66 * durationScale, stagger: 0.055, ease: 'power3.out',
            clearProps: 'opacity,visibility,transform' },
          0.07
        );
      }
      return;
    }

    const firstItems = getIntroItems(firstSection).slice(0, 12);
    if (!firstItems.length) return;

    markMotionHandled(firstItems);
    animateContainedMedia(gsap, firstItems, { duration: 0.66, stagger: 0.035 });

    tl.fromTo(
      firstItems,
      { autoAlpha: 0, y: 22 },
      { autoAlpha: 1, y: 0,
        duration: 0.62 * durationScale, stagger: 0.045, ease: 'power3.out',
        clearProps: 'opacity,visibility,transform' }
    );
  }


  /* ─────────────────────────────────────────
   *  SCROLL REVEALS
   * ───────────────────────────────────────── */
  function setupScrollReveals(gsap, ScrollTrigger, root) {
    const candidates = getMotionCandidates(root, MAIN_REVEAL_SELECTOR)
      .filter((el) => !el.dataset.ziliusReveal)
      .filter((el) => !el.closest(MOTION_EXCLUDE_SELECTOR))
      .slice(0, 48);

    if (!candidates.length) return;

    candidates.forEach((el) => {
      el.dataset.ziliusReveal = 'pending';
      el.classList.add('zilius-reveal-item');
    });

    gsap.set(candidates, { autoAlpha: 0, y: (_index, el) => getRevealStartY(el) });

    if (ScrollTrigger) {
      ScrollTrigger.batch(candidates, {
        start: 'top 94%', once: true, interval: 0.06, batchMax: 6,
        onEnter: (batch) => revealBatch(gsap, batch),
      });
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        revealBatch(gsap, [entry.target]);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.15 });

    candidates.forEach((el) => observer.observe(el));
  }

  function revealBatch(gsap, batch) {
    const elements = batch.filter((el) => el.dataset.ziliusReveal !== 'done');
    if (!elements.length) return;

    elements.forEach((el) => { el.dataset.ziliusReveal = 'active'; });
    animateContainedMedia(gsap, elements, { duration: 0.56, stagger: 0.025 });

    gsap.to(elements, {
      autoAlpha: 1, y: 0,
      duration: 0.5, stagger: 0.035, ease: 'power3.out', overwrite: 'auto',
      onComplete() {
        elements.forEach((el) => {
          el.dataset.ziliusReveal = 'done';
          el.classList.remove('zilius-reveal-item');
        });
        gsap.set(elements, { clearProps: 'opacity,visibility,transform' });
      },
    });
  }


  /* ─────────────────────────────────────────
   *  ★ WORD REVEAL — titoli animati per parola
   *  Applica automaticamente a h1, h2, .hero__heading
   *  Tecnica: split custom senza plugin, clip-path reveal
   * ───────────────────────────────────────── */
  function setupMediaCardGalleryReveals(gsap, ScrollTrigger, root) {
    const sections = getUniqueElements(root.querySelectorAll('#MainContent .media-card-gallery-section'))
      .filter((section) => !section.dataset.ziliusMediaCardGalleryReveal)
      .filter((section) => !section.closest(MOTION_EXCLUDE_SELECTOR))
      .filter(hasMotionBox)
      .slice(0, 12);

    if (!sections.length) return;

    sections.forEach((section) => {
      const slides = getUniqueElements(section.querySelectorAll('.media-card-gallery__slide'));
      const cards = getUniqueElements(
        slides.map((slide) => slide.querySelector('.media-card-gallery__card')).filter(Boolean),
      );
      const controls = getUniqueElements(section.querySelectorAll('.media-card-gallery__controls-wrap')).filter(hasMotionBox);
      const targets = [...cards, ...controls];

      if (!targets.length) return;

      section.dataset.ziliusMediaCardGalleryReveal = 'pending';
      targets.forEach((target) => {
        target.dataset.ziliusReveal = 'pending';
        target.classList.add('zilius-reveal-item');
      });

      if (cards.length) {
        gsap.set(cards, { autoAlpha: 0, x: 42, y: 24 });
      }

      if (controls.length) {
        gsap.set(controls, { autoAlpha: 0, y: 16 });
      }

      const reveal = () => {
        if (section.dataset.ziliusMediaCardGalleryReveal === 'done') return;
        section.dataset.ziliusMediaCardGalleryReveal = 'done';

        targets.forEach((target) => { target.dataset.ziliusReveal = 'active'; });
        slides.forEach((slide) => { slide.style.contentVisibility = 'visible'; });
        animateContainedMedia(gsap, cards, { duration: 0.78, stagger: 0.045 });

        const timeline = gsap.timeline({
          defaults: { ease: 'power3.out', overwrite: 'auto' },
          onComplete() {
            targets.forEach((target) => {
              target.dataset.ziliusReveal = 'done';
              target.classList.remove('zilius-reveal-item');
            });
            slides.forEach((slide) => { slide.style.contentVisibility = ''; });
            gsap.set(targets, { clearProps: 'opacity,visibility,transform' });
          },
        });

        if (cards.length) {
          timeline.to(cards, {
            autoAlpha: 1, x: 0, y: 0,
            duration: 0.76, stagger: { each: 0.095, from: 'start' },
          }, 0);
        }

        if (controls.length) {
          timeline.to(controls, {
            autoAlpha: 1, y: 0,
            duration: 0.48,
          }, 0.22);
        }
      };

      if (ScrollTrigger) {
        ScrollTrigger.create({
          trigger: section,
          start: 'top 90%',
          once: true,
          onEnter: reveal,
        });
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(section);
          reveal();
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });

      observer.observe(section);
    });
  }

  function setupWordReveal(gsap, ScrollTrigger) {
    if (!ScrollTrigger) return;

    const wordRevealSelector = [
      '#MainContent .hero__content-wrapper h1',
      '#MainContent .hero__content-wrapper h2',
      '#MainContent .section-content-wrapper > header h1',
      '#MainContent .section-content-wrapper > header h2',
      '#MainContent .text-block h1',
      '#MainContent .text-block h2',
      '#MainContent .text-block p',
      '#MainContent .text-block li',
    ].join(', ');

    const textTargets = Array.from(document.querySelectorAll(wordRevealSelector))
      .filter((el) => !el.dataset.ziliusWordReveal)
      .filter(hasMotionBox)
      .filter((el) => !el.closest(MOTION_EXCLUDE_SELECTOR))
      .filter(isWordRevealCandidate)
      .slice(0, 70);

    textTargets.forEach((target) => {
      target.dataset.ziliusWordReveal = 'pending';

      const originalHTML = target.innerHTML;
      const wordSpans = wrapWordsForReveal(target);
      if (!wordSpans.length) {
        target.dataset.ziliusWordReveal = 'done';
        return;
      }

      const isBodyText = target.matches('p, li');
      gsap.set(wordSpans, { yPercent: isBodyText ? 76 : 105, opacity: 0 });

      ScrollTrigger.create({
        trigger: target,
        start: 'top 90%',
        once: true,
        onEnter() {
          target.dataset.ziliusWordReveal = 'done';
          gsap.to(wordSpans, {
            yPercent: 0,
            opacity: 1,
            duration: isBodyText ? 0.58 : 0.72,
            stagger: isBodyText ? 0.018 : 0.045,
            ease: 'power3.out',
            onComplete() {
              target.innerHTML = originalHTML;
            },
          });
        },
      });
    });
  }


  /* ─────────────────────────────────────────
   *  ★ MAGNETIC BUTTONS
   *  I bottoni primari attirano il cursore
   *  Solo su dispositivi desktop (fine pointer)
   * ───────────────────────────────────────── */
  function setupMagneticButtons(gsap) {
    if (!finePointerQuery.matches) return;

    const magneticSelector = [
      '.button:not([disabled])',
      '.button-secondary:not([disabled])',
      '.quick-add__button:not([disabled])',
    ].join(', ');

    const STRENGTH = 0.38;
    const RADIUS   = 100; // px — zona di attrazione

    document.addEventListener('mousemove', (e) => {
      document.querySelectorAll(magneticSelector).forEach((btn) => {
        const rect   = btn.getBoundingClientRect();
        const cx     = rect.left + rect.width  / 2;
        const cy     = rect.top  + rect.height / 2;
        const dx     = e.clientX - cx;
        const dy     = e.clientY - cy;
        const dist   = Math.sqrt(dx * dx + dy * dy);

        if (dist < RADIUS) {
          const factor = (1 - dist / RADIUS) * STRENGTH;
          gsap.to(btn, {
            x: dx * factor,
            y: dy * factor,
            duration: 0.35,
            ease: 'power2.out',
            overwrite: 'auto',
          });
        } else if (btn._magneticActive) {
          btn._magneticActive = false;
          gsap.to(btn, { x: 0, y: 0, duration: 0.45, ease: 'elastic.out(1,0.4)', overwrite: 'auto',
            onComplete: () => gsap.set(btn, { clearProps: 'x,y' }) });
        }

        btn._magneticActive = dist < RADIUS;
      });
    }, { passive: true });
  }


  /* ─────────────────────────────────────────
   *  ★ 3D CARD TILT
   *  Le card prodotto/collezione hanno un effetto
   *  prospettica 3D al passaggio del mouse
   * ───────────────────────────────────────── */
  function setupCardTilt3D(gsap) {
    if (!finePointerQuery.matches) return;

    const cardSelector = [
      '.product-card',
      '.resource-card',
      '.featured-blog-posts-card',
    ].join(', ');

    const MAX_TILT = 8; // gradi massimi
    const SCALE    = 1.028;

    document.addEventListener('mousemove', (e) => {
      const card = e.target.closest(cardSelector);
      if (!card || !isEligibleElement(card)) return;

      const rect = card.getBoundingClientRect();
      const rx   = ((e.clientY - rect.top)  / rect.height - 0.5) * 2;
      const ry   = ((e.clientX - rect.left) / rect.width  - 0.5) * 2;

      gsap.to(card, {
        rotateX: -rx * MAX_TILT,
        rotateY:  ry * MAX_TILT,
        scale:    SCALE,
        transformPerspective: 800,
        duration: 0.4,
        ease: 'power2.out',
        overwrite: 'auto',
      });
    }, { passive: true });

    document.addEventListener('mouseleave', (e) => {
      const card = e.target.closest(cardSelector);
      if (!card) return;

      gsap.to(card, {
        rotateX: 0, rotateY: 0, scale: 1,
        duration: 0.55, ease: 'elastic.out(1, 0.5)', overwrite: 'auto',
        onComplete: () => gsap.set(card, { clearProps: 'rotateX,rotateY,scale,transformPerspective' }),
      });
    }, true);
  }


  /* ─────────────────────────────────────────
   *  ★ COUNTER ANIMATO
   *  Cerca elementi con data-count o testo numerico
   *  puro. Es: <span data-zilius-count="247">0</span>
   *  oppure <span data-zilius-count>98%</span>
   * ───────────────────────────────────────── */
  function setupCounters(gsap, ScrollTrigger) {
    if (!ScrollTrigger) return;

    const counterEls = Array.from(
      document.querySelectorAll('[data-zilius-count]')
    ).filter((el) => !el.dataset.ziliusCountDone);

    counterEls.forEach((el) => {
      el.dataset.ziliusCountDone = 'pending';

      const rawText  = el.dataset.ziliusCount || el.textContent.trim();
      const numMatch = rawText.match(/[\d.,]+/);
      if (!numMatch) return;

      const target   = parseFloat(numMatch[0].replace(',', '.'));
      const prefix   = rawText.slice(0, rawText.indexOf(numMatch[0]));
      const suffix   = rawText.slice(rawText.indexOf(numMatch[0]) + numMatch[0].length);
      const decimals = (numMatch[0].includes('.') || numMatch[0].includes(','))
        ? numMatch[0].split(/[.,]/)[1].length : 0;

      el.textContent = prefix + '0' + suffix;

      ScrollTrigger.create({
        trigger: el,
        start: 'top 88%',
        once: true,
        onEnter() {
          el.dataset.ziliusCountDone = 'done';
          const obj = { val: 0 };
          gsap.to(obj, {
            val: target,
            duration: 1.8,
            ease: 'power2.out',
            onUpdate() {
              el.textContent = prefix + obj.val.toFixed(decimals).replace('.', ',') + suffix;
            },
          });
        },
      });
    });
  }


  /* ─────────────────────────────────────────
   *  ★ NAVBAR HIDE/SHOW SU SCROLL
   *  Header si nasconde scrollando giù, riappare
   *  scrollando su. Smooth GSAP, non CSS.
   * ───────────────────────────────────────── */
  function setupNavbarScrollBehavior(gsap, ScrollTrigger) {
    if (!ScrollTrigger) return;

    const header = document.querySelector('#header-group') ||
                   document.querySelector('header') ||
                   document.querySelector('.shopify-section-group-header-group');
    if (!header) return;

    let lastScroll    = 0;
    let headerVisible = true;
    const headerH     = header.offsetHeight || 80;

    ScrollTrigger.create({
      start: 'top top',
      end: 99999,
      onUpdate(self) {
        const current   = self.scroll();
        const delta     = current - lastScroll;
        const scrollingDown = delta > 0;

        if (current < headerH * 1.5) {
          if (!headerVisible) {
            headerVisible = true;
            gsap.to(header, { yPercent: 0, duration: 0.35, ease: 'power3.out', overwrite: 'auto' });
          }
          lastScroll = current;
          return;
        }

        if (scrollingDown && Math.abs(delta) > 4 && headerVisible) {
          headerVisible = false;
          gsap.to(header, { yPercent: -100, duration: 0.38, ease: 'power3.inOut', overwrite: 'auto' });
        } else if (!scrollingDown && Math.abs(delta) > 4 && !headerVisible) {
          headerVisible = true;
          gsap.to(header, { yPercent: 0, duration: 0.42, ease: 'power3.out', overwrite: 'auto' });
        }

        lastScroll = current;
      },
    });

    // Garantisci che l'header sia sticky
    if (getComputedStyle(header).position === 'static') {
      header.style.position = 'sticky';
      header.style.top      = '0';
      header.style.zIndex   = '100';
    }
  }


  /* ─────────────────────────────────────────
   *  ★ HERO PARALLAX MULTI-LAYER
   *  Il testo si muove più lentamente dell'immagine
   *  creando profondità cinematografica
   * ───────────────────────────────────────── */
  function setupHeroParallaxText(gsap, ScrollTrigger) {
    if (!ScrollTrigger || !finePointerQuery.matches) return;

    const hero = document.querySelector('#MainContent .hero');
    if (!hero) return;
    if (hero.classList.contains('hero--auto')) return;

    const media = hero.querySelector('.hero__media-wrapper');

    // Text-only heroes use a placeholder background. Moving it exposes a blank
    // strip below the sticky header, so only animate real hero images or video.
    if (!media?.querySelector('img, video')) return;

    const content = hero.querySelector('.hero__content-wrapper');

    if (media) {
      gsap.fromTo(media,
        { yPercent: 0 },
        { yPercent: 18,
          ease: 'none',
          scrollTrigger: {
            trigger: hero, start: 'top top', end: 'bottom top', scrub: 0.6,
          },
        }
      );
    }

    if (content) {
      gsap.fromTo(content,
        { yPercent: 0 },
        { yPercent: 8,
          ease: 'none',
          scrollTrigger: {
            trigger: hero, start: 'top top', end: 'bottom top', scrub: 0.4,
          },
        }
      );
    }
  }

  /* ─────────────────────────────────────────
   *  IMAGE DEPTH PARALLAX (invariato)
   * ───────────────────────────────────────── */
  function setupImageDepth(gsap, ScrollTrigger, root) {
    if (!ScrollTrigger || !finePointerQuery.matches) return;

    const frames = getUniqueElements(root.querySelectorAll([
      '#MainContent .image-block',
      '#MainContent .resource-image',
      '#MainContent .product-media-container--image',
      '#MainContent .media-card-gallery__image',
      '#MainContent .background-image-container',
    ].join(', ')))
      .filter((f) => !f.dataset.ziliusDepthFrame)
      .filter(isEligibleElement)
      .filter((f) => !f.closest('.product-card, .collection-card, .resource-card, .mobile-home-hero-cards-rail, .mobile-page-menu-hero-cards-rail'))
      .filter((frame) => frame.getBoundingClientRect().top > window.innerHeight * 0.75)
      .slice(0, 6);

    frames.forEach((frame) => {
      const media = frame.querySelector('img, video');
      if (!media || media.dataset.ziliusDepthMedia || !isEligibleElement(media)) return;
      if (media instanceof HTMLImageElement && media.loading === 'lazy' && !media.complete) return;

      frame.dataset.ziliusDepthFrame = 'true';
      frame.classList.add('zilius-motion-media-frame');
      media.dataset.ziliusDepthMedia = 'true';
      media.classList.add('zilius-motion-depth-media');

      gsap.fromTo(media,
        { yPercent: -3.5, scale: 1.055 },
        { yPercent: 3.5, scale: 1.015, ease: 'none',
          scrollTrigger: { trigger: frame, start: 'top bottom', end: 'bottom top', scrub: 0.7 },
        }
      );
    });
  }


  /* ─────────────────────────────────────────
   *  CUSTOM CURSOR — disabilitato
   * ───────────────────────────────────────── */
  function injectCursorStyles() { /* cursore nativo */ }
  function setupCustomCursor()  { /* cursore nativo */ }


  /* ─────────────────────────────────────────
   *  IMAGE LOAD MOTION (invariato)
   * ───────────────────────────────────────── */
  function setupImageLoadMotion(gsap) {
    const prepareImages = (root) => {
      const images = [];
      if (root instanceof HTMLImageElement) { images.push(root); }
      else if (root instanceof Element || root instanceof Document) {
        images.push(...root.querySelectorAll(root instanceof Document ? IMAGE_LOAD_SELECTOR : 'img'));
      }
      images.forEach((img) => prepareImageLoadMotion(gsap, img));
    };

    prepareImages(document);

    document.addEventListener('load', (e) => {
      if (e.target instanceof HTMLImageElement) revealLoadedImage(gsap, e.target);
    }, true);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        m.addedNodes.forEach((node) => {
          if (node instanceof Element) prepareImages(node);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function prepareImageLoadMotion(gsap, image) {
    if (!(image instanceof HTMLImageElement)) return;
    if (image.dataset.ziliusImageMotion) return;
    if (image.closest(MOTION_EXCLUDE_SELECTOR)) return;
    if (!image.closest('#MainContent')) return;

    image.dataset.ziliusImageMotion = 'ready';
    if (image.complete && image.naturalWidth > 0) return;

    image.dataset.ziliusImageMotion = 'loading';
    image.classList.add('zilius-image-load-pending');
    gsap.set(image, { autoAlpha: 0, scale: 1.01, transformOrigin: 'center center' });

    image.addEventListener('load',  () => revealLoadedImage(gsap, image),    { once: true });
    image.addEventListener('error', () => resetImageLoadMotion(gsap, image), { once: true });
  }

  function revealLoadedImage(gsap, image) {
    if (!(image instanceof HTMLImageElement)) return;
    if (image.dataset.ziliusImageMotion !== 'loading') return;
    image.dataset.ziliusImageMotion = 'ready';
    image.classList.remove('zilius-image-load-pending');
    gsap.to(image, { autoAlpha: 1, scale: 1, duration: 0.55,
      ease: 'power2.out', overwrite: 'auto', clearProps: 'opacity,visibility,transform,transformOrigin' });
  }

  function resetImageLoadMotion(gsap, image) {
    if (!(image instanceof HTMLImageElement)) return;
    image.dataset.ziliusImageMotion = 'ready';
    image.classList.remove('zilius-image-load-pending');
    gsap.set(image, { clearProps: 'opacity,visibility,transform,filter,transformOrigin' });
  }


  /* ─────────────────────────────────────────
   *  ANIMATE CONTAINED MEDIA (invariato)
   * ───────────────────────────────────────── */
  function animateContainedMedia(gsap, elements, options = {}) {
    const media = [];
    elements.forEach((el) => {
      if (el instanceof HTMLImageElement || el instanceof HTMLVideoElement) { media.push(el); return; }
      media.push(...el.querySelectorAll('img, video'));
    });

    const targets = getUniqueElements(media)
      .filter((el) => !el.dataset.ziliusRevealMedia)
      .filter((el) => el.dataset.ziliusImageMotion !== 'loading')
      .filter((el) => !el.closest(MOTION_EXCLUDE_SELECTOR));

    if (!targets.length) return;
    targets.forEach((el) => { el.dataset.ziliusRevealMedia = 'true'; });

    gsap.fromTo(targets,
      { scale: 1.018, transformOrigin: 'center center' },
      { scale: 1, duration: options.duration || 0.68,
        stagger: options.stagger || 0.03, ease: 'power2.out', overwrite: 'auto',
        clearProps: 'transform,transformOrigin' }
    );
  }

  /* ─────────────────────────────────────────
   *  INTERACTIVE MOTION (invariato)
   * ───────────────────────────────────────── */
  function setupInteractiveMotion(gsap) {
    const interactiveSelector = [
      '.button:not(.header-actions__action):not(.close-button)',
      '.button-secondary:not(.header-actions__action)',
      'button.shopify-payment-button__button--unbranded',
      '.slideshow-control',
      '.quantity-selector button', '.pagination__link',
      '.facets__pill-label', '.variant-option__button-label',
      '.search__reset-button',
    ].join(', ');

    const cardSelector = [
      '.product-card', '.resource-card',
      '.featured-blog-posts-card', '.blog-post-card', '.blog-post-item',
      '.mobile-home-hero-cards-rail .hero__content-wrapper > .group-block > .group-block-content > .group-block',
      '.mobile-page-menu-hero-cards-rail .hero__content-wrapper > .group-block > .group-block-content > .group-block',
    ].join(', ');

    document.addEventListener('pointerenter', (e) => {
      if (!finePointerQuery.matches) return;
      const interactive = closestEnabled(e.target, interactiveSelector);
      if (interactive) {
        if (isInternalPointerTransition(interactive, e)) return;
        gsap.to(interactive, { y: -2, scale: 1.018, duration: 0.24, ease: 'power2.out', overwrite: 'auto' });
        return;
      }
      const card = closestEnabled(e.target, cardSelector);
      if (card && !isInternalPointerTransition(card, e)) animateCardMedia(gsap, card, true);
    }, true);

    document.addEventListener('pointerleave', (e) => {
      const interactive = closestEnabled(e.target, interactiveSelector);
      if (interactive) {
        if (isInternalPointerTransition(interactive, e)) return;
        gsap.to(interactive, { x: 0, y: 0, scale: 1, duration: 0.28, ease: 'power2.out', overwrite: 'auto', clearProps: 'transform' });
        return;
      }
      const card = closestEnabled(e.target, cardSelector);
      if (card && !isInternalPointerTransition(card, e)) animateCardMedia(gsap, card, false);
    }, true);

    document.addEventListener('pointerdown', (e) => {
      const interactive = closestEnabled(e.target, interactiveSelector);
      if (!interactive) return;
      gsap.to(interactive, { y: 0, scale: 0.975, duration: 0.12, ease: 'power2.out', overwrite: 'auto' });
    }, true);

    document.addEventListener('pointerup', (e) => {
      const interactive = closestEnabled(e.target, interactiveSelector);
      if (!interactive || !finePointerQuery.matches) return;
      gsap.to(interactive, { y: -2, scale: 1.018, duration: 0.18, ease: 'power2.out', overwrite: 'auto' });
    }, true);

    document.addEventListener('focusin', (e) => {
      const interactive = closestEnabled(e.target, interactiveSelector);
      if (!interactive) return;
      gsap.to(interactive, { y: -2, scale: 1.012, duration: 0.22, ease: 'power2.out', overwrite: 'auto' });
    });
    document.addEventListener('focusout', (e) => {
      const interactive = closestEnabled(e.target, interactiveSelector);
      if (!interactive) return;
      gsap.to(interactive, { y: 0, scale: 1, duration: 0.22, ease: 'power2.out', overwrite: 'auto', clearProps: 'transform' });
    });
  }


  /* ─────────────────────────────────────────
   *  CARD MEDIA ANIMATION (invariato)
   * ───────────────────────────────────────── */
  function animateCardMedia(gsap, card, isEntering) {
    const media = card.querySelector([
      '.card-gallery img', '.product-card__image img',
      '.collection-card__image img', '.resource-card__image img',
      '.featured-blog-posts-card__image img', '.blog-post-card__image-container img',
      '.image-block__image', '.background-image-container img',
    ].join(', '));

    if (!media || !isEligibleElement(media)) return;
    media.classList.add('zilius-motion-card-media');

    const tween = {
      scale: isEntering ? 1.045 : 1,
      duration: isEntering ? 0.52 : 0.42,
      ease: 'power3.out', overwrite: 'auto',
    };
    if (!isEntering) tween.clearProps = 'transform';
    gsap.to(media, tween);
  }

  /* ─────────────────────────────────────────
   *  SHOPIFY EDITOR REFRESH (invariato)
   * ───────────────────────────────────────── */
  function setupShopifyEditorRefresh(gsap, ScrollTrigger) {
    document.addEventListener('shopify:section:load', (event) => {
      if (!(event.target instanceof HTMLElement)) return;
      window.setTimeout(() => {
        enhanceMediaFrames(event.target);
        setupScrollReveals(gsap, ScrollTrigger, document);
        setupMediaCardGalleryReveals(gsap, ScrollTrigger, document);
        setupCounters(gsap, ScrollTrigger);
        scheduleNonCriticalMotion(() => setupImageDepth(gsap, ScrollTrigger, document));
        refreshScrollTriggers(ScrollTrigger);
      }, 120);
    });
  }

  /* ─────────────────────────────────────────
   *  ENHANCE MEDIA FRAMES (invariato)
   * ───────────────────────────────────────── */
  function enhanceMediaFrames(root = document) {
    root.querySelectorAll([
      '.image-block', '.card-gallery', '.collection-card__image',
      '.resource-card__image', '.featured-blog-posts-card__image',
      '.product-media-container--image', '.hero__media-wrapper',
      '.media-card-gallery__card', '.media-card-gallery__media',
    ].join(', ')).forEach((frame) => frame.classList.add('zilius-motion-media-frame'));
  }

  /* ─────────────────────────────────────────
   *  UTILITY FUNCTIONS
   * ───────────────────────────────────────── */
  function getHeroNavigationItems(hero) {
    return getUniqueElements(hero.querySelectorAll('.hero__content-wrapper > .group-block')).filter((block) => {
      const cards     = getUniqueElements(block.querySelectorAll(':scope > .group-block-content > .group-block'));
      if (cards.length < 3) return false;
      const imgCards  = cards.filter((c) => c.querySelector('.image-block, .group-block__media-wrapper')).length;
      const lnkCards  = cards.filter((c) => c.querySelector('a[href]')).length;
      return imgCards >= Math.min(3, cards.length) && lnkCards >= Math.min(2, cards.length);
    });
  }

  function getIntroItems(firstSection) {
    if (!(firstSection instanceof HTMLElement)) return [];
    return getMotionCandidates(firstSection, FIRST_SECTION_INTRO_SELECTOR)
      .filter((el) => !el.closest(MOTION_EXCLUDE_SELECTOR))
      .filter((el) => !isDeepBelowFold(el));
  }

  function closestEnabled(target, selector) {
    if (!(target instanceof Element)) return null;
    const el = target.closest(selector);
    if (!(el instanceof HTMLElement)) return null;
    if (el.matches('[disabled], [aria-disabled="true"]')) return null;
    if (el.closest('[disabled], [aria-disabled="true"]')) return null;
    return el;
  }

  function isInternalPointerTransition(element, event) {
    return event.relatedTarget instanceof Node && element.contains(event.relatedTarget);
  }

  function isEligibleElement(element) {
    if (!(element instanceof Element)) return false;
    const rect  = element.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) !== 0;
  }

  function hasMotionBox(element) {
    if (!(element instanceof Element)) return false;
    const rect = element.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return false;
    return window.getComputedStyle(element).display !== 'none';
  }

  function isDeepBelowFold(element) {
    return element.getBoundingClientRect().top > window.innerHeight * 1.15;
  }

  function getRevealStartY(element) {
    if (isTextInAutoHeightHero(element)) return 0;
    return 20;
  }

  function isTextInAutoHeightHero(element) {
    return (
      element instanceof Element &&
      element.matches('.text-block, rte-formatter') &&
      Boolean(element.closest('.hero--auto .hero__container'))
    );
  }

  function wrapWordsForReveal(element) {
    const textNodes = [];
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
        if (!(node.parentElement instanceof Element)) return NodeFilter.FILTER_REJECT;
        if (node.parentElement.closest('script, style, noscript, .z-word, .z-word-wrap')) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    while (walker.nextNode()) textNodes.push(walker.currentNode);

    const wordSpans = [];

    textNodes.forEach((node) => {
      const fragment = document.createDocumentFragment();
      const tokens = node.nodeValue.split(/(\s+)/);

      tokens.forEach((token) => {
        if (!token) return;
        if (/^\s+$/.test(token)) {
          fragment.appendChild(document.createTextNode(token));
          return;
        }

        const wrap = document.createElement('span');
        wrap.className = 'z-word-wrap';
        wrap.style.display = 'inline-block';
        wrap.style.overflow = 'hidden';
        wrap.style.verticalAlign = 'bottom';

        const word = document.createElement('span');
        word.className = 'z-word';
        word.style.display = 'inline-block';
        word.textContent = token;

        wrap.appendChild(word);
        fragment.appendChild(wrap);
        wordSpans.push(word);
      });

      node.parentNode.replaceChild(fragment, node);
    });

    return wordSpans;
  }

  function isWordRevealCandidate(element) {
    if (!(element instanceof Element)) return false;
    if (element.closest('.product-card, .collection-card, .resource-card, .quick-add-modal, .cart-items__table')) {
      return false;
    }

    const text = element.textContent.trim();
    if (!text) return false;

    if (element.matches('p, li')) {
      const wordCount = text.split(/\s+/).length;
      return wordCount <= 42;
    }

    return true;
  }

  function isSameOriginNavigation() {
    if (!document.referrer) return false;

    try {
      return new URL(document.referrer).origin === window.location.origin;
    } catch (_error) {
      return false;
    }
  }

  function isShopifyDesignMode() {
    return Boolean(window.Shopify?.designMode || document.documentElement.classList.contains('shopify-design-mode'));
  }

  function markMotionHandled(elements) {
    elements.forEach((el) => { el.dataset.ziliusReveal = 'done'; });
  }

  function getMotionCandidates(root, selector) {
    const elements     = getUniqueElements(root.querySelectorAll(selector)).filter(isEligibleElement);
    const repeatedItems = elements.filter(isRepeatedMotionElement);
    return elements.filter((el) => {
      if (isRepeatedMotionElement(el)) return true;
      return !repeatedItems.some((item) => el !== item && el.contains(item));
    });
  }

  function isRepeatedMotionElement(element) {
    return element.matches('.product-grid__item, .resource-list__item, .blog-post-item, .media-card-gallery__card');
  }

  function getUniqueElements(nodeList) {
    return Array.from(new Set(Array.from(nodeList))).filter((el) => el instanceof HTMLElement);
  }

  function refreshScrollTriggers(ScrollTrigger) {
    if (!ScrollTrigger) return;
    window.setTimeout(() => ScrollTrigger.refresh(), 120);
  }

  function scheduleNonCriticalMotion(callback) {
    const run = () => {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(callback, { timeout: 1400 });
        return;
      }
      window.setTimeout(callback, 220);
    };
    if (document.readyState === 'complete') { run(); return; }
    window.addEventListener('load', run, { once: true });
  }

  /* ─────────────────────────────────────────
   *  AVVIO
   * ───────────────────────────────────────── */
  onReady(waitForGsap);

})();
